// src/pages.lazy.jsx
// Browser-side page map: every page is its own chunk, loaded on first visit.
// Keys must match routeTable in Routes.jsx (and the eager map in entry-server.jsx).
import React, { lazy } from "react";
import { pageKeyFor } from "./Routes";

const RELOAD_FLAG = "chunk-reload";

// After a deploy, a tab that was already open asks for chunk files that no longer
// exist. Reload once to pick up the new build instead of showing an error.
const withReload = (load) => () =>
  load()
    .then((module) => {
      sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    })
    .catch((error) => {
      if (sessionStorage.getItem(RELOAD_FLAG)) throw error;
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return new Promise(() => {});
    });

// A lazy page that can also be loaded ahead of the first render (see preloadPage),
// so the prerendered HTML is never replaced by an empty Suspense fallback.
const lazyPage = (importer) => {
  const load = withReload(importer);
  const Lazy = lazy(load);
  let Loaded = null;

  const Page = (props) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />);
  Page.preload = () =>
    load().then((module) => {
      Loaded = module.default;
    });
  return Page;
};

export const pages = {
  Homepage: lazyPage(() => import("./pages/homepage")),
  Contact: lazyPage(() => import("./pages/contact")),
  HealthBenefitsPage: lazyPage(() => import("./pages/health-benefits")),
  AntioxidantPowerDetail: lazyPage(() => import("./pages/health-benefits/educational/antioxidant-power-detail")),
  HeartHealthDetail: lazyPage(() => import("./pages/health-benefits/educational/heart-health-detail")),
  EventsPage: lazyPage(() => import("./pages/events")),
  EventDetailsPage: lazyPage(() => import("./pages/events/EventDetailsPage")),
  RecipesPage: lazyPage(() => import("./pages/recipes")),
  RecipeDetailsPage: lazyPage(() => import("./pages/recipes/RecipeDetailsPage")),
  BlogHub: lazyPage(() => import("./pages/blog-hub")),
  BlogDetail: lazyPage(() => import("./pages/blog/BlogDetail")),
  UrinaryTractHealthDetailPage: lazyPage(() => import("./pages/health-benefits/educational/urinary-tract-health-detail")),
  ImmuneSystemBoost: lazyPage(() => import("./pages/health-benefits/educational/immune-system-boost")),
  DigestiveWellness: lazyPage(() => import("./pages/health-benefits/educational/digestive-wellness")),
  AntiInflammatoryEffects: lazyPage(() => import("./pages/health-benefits/educational/anti-inflammatory-effects")),
  PrivacyPolicy: lazyPage(() => import("./pages/legal/PrivacyPolicy")),
  Terms: lazyPage(() => import("./pages/legal/Terms")),
  About: lazyPage(() => import("./pages/about")),
};

// Resolves once the page for this pathname is loaded (immediately for unknown paths).
export const preloadPage = (pathname) => {
  const key = pageKeyFor(pathname);
  return key ? pages[key].preload() : Promise.resolve();
};
